/**
 * Υπο-καρτέλα «Τιμές» — σύγκριση efood ↔ κατάλογος (κανάλι delivery) και οι
 * ΜΟΝΙΜΕΣ αποφάσεις του χειριστή ανά κεντρικό είδος (§13.19.Α + Β, §11.1).
 *
 * ΓΙΑΤΙ ΞΕΧΩΡΙΣΤΗ ΟΘΟΝΗ ΚΑΙ ΟΧΙ ΑΥΤΟΜΑΤΗ ΔΙΟΡΘΩΣΗ: οι 143 αποκλίσεις που
 * μετρήθηκαν στα 20 καταστήματα είναι ΣΥΣΤΗΜΑΤΙΚΕΣ ανά προϊόν (ίδιες παντού) —
 * δηλαδή εμπορικές αποφάσεις, όχι σφάλματα. Ένα «συγχρόνισε τις τιμές» χωρίς
 * ανθρώπινη απόφαση θα κατέβαζε 140 τιμές που κάποιος είχε ανεβάσει επίτηδες.
 *
 * Η ταξινόμηση είναι |διαφορά| ΦΘΙΝΟΥΣΑ, ώστε οι 140 γραμμές «+0,10» να
 * διαβάζονται ως μία ομάδα και να αποφασίζονται μαζικά.
 */

import React, { useMemo } from 'react';
import { AlertTriangle, Coins, Loader2, RefreshCw, Save, Search, Undo2 } from 'lucide-react';
import {
  PriceDiffRow, PriceOverride, StoreSummaryAny,
} from '../../services/platformCatalogApi';
import {
  fmtDelta, fmtMoney, OVERRIDE_MODE_CLASSES, OVERRIDE_MODE_LABELS, PRICE_REASON_LABELS,
  PriceIndex, PriceMasterInfo, sortMastersByDelta, storeShort,
} from './catalogUi';
import PriceDecision from './PriceDecision';

interface Props {
  stores: StoreSummaryAny[];
  /** null = όλα τα καταστήματα του οργανισμού. */
  storeId: number | null;
  onStoreChange: (id: number | null) => void;
  onlyMismatch: boolean;
  onOnlyMismatchChange: (v: boolean) => void;
  q: string;
  onQChange: (v: string) => void;
  index: PriceIndex | null;
  summary: Record<string, unknown> | null;
  total: number;
  loading: boolean;
  /** Ο server δεν έχει (ακόμη) τη διαδρομή — μόνιμο μήνυμα, όχι άδειος πίνακας. */
  unavailable: string | null;
  /**
   * Ζητήθηκε η σύγκριση σε αυτή τη συνεδρία; Όσο είναι `false` ο πίνακας είναι
   * άδειος ΕΠΕΙΔΗ δεν ρωτήθηκε κανείς — και το λέει, αντί να διαβάζεται ως
   * «όλες οι τιμές συμφωνούν» (το χειρότερο δυνατό ψέμα εδώ).
   */
  requested?: boolean;
  /** org_admin: μόνο αυτός γράφει αποφάσεις (PUT …/price-overrides). */
  canDecide: boolean;
  /** org_editor + κατάσταση «επεξεργασία»: δημιουργία πρόχειρων αλλαγών. */
  canSync: boolean;
  draftFor: (masterKey: string) => PriceOverride;
  isDirty: (masterKey: string) => boolean;
  onDraftChange: (next: PriceOverride) => void;
  dirtyCount: number;
  saving: boolean;
  onSave: () => void;
  onDiscardDrafts: () => void;
  onReload: () => void;
  onOpenSync: () => void;
}

const num = (obj: Record<string, unknown> | null, key: string): number | null => {
  if (!obj) return null;
  const v = obj[key];
  return (typeof v === 'number' && isFinite(v)) ? v : (typeof v === 'string' && v !== '' && isFinite(Number(v)) ? Number(v) : null);
};

/** Εύρος τιμών πλατφόρμας του είδους (min–max σε όλη την αλυσίδα). */
const platformRange = (rows: PriceDiffRow[]): { min: number | null; max: number | null } => {
  let min: number | null = null;
  let max: number | null = null;
  for (let i = 0; i < rows.length; i++) {
    const p = rows[i].platform_price;
    if (p === null || p === undefined) continue;
    const n = Number(p);
    if (!isFinite(n)) continue;
    min = min === null ? n : Math.min(min, n);
    max = max === null ? n : Math.max(max, n);
  }
  return { min, max };
};

const MasterRow: React.FC<{
  master: PriceMasterInfo;
  open: boolean;
  onToggle: () => void;
  storeNames: Record<string, string>;
  draft: PriceOverride;
  dirty: boolean;
  canDecide: boolean;
  onDraftChange: (next: PriceOverride) => void;
}> = ({ master, open, onToggle, storeNames, draft, dirty, canDecide, onDraftChange }) => {
  const pr = platformRange(master.rows);
  const catalogText = master.catalog_min === null
    ? '—'
    : (master.catalog_max !== null && master.catalog_max !== master.catalog_min
      ? `${fmtMoney(master.catalog_min)} – ${fmtMoney(master.catalog_max)}`
      : fmtMoney(master.catalog_min));
  const platformText = pr.min === null
    ? '—'
    : (pr.max !== null && pr.max !== pr.min ? `${fmtMoney(pr.min)} – ${fmtMoney(pr.max)}` : fmtMoney(pr.min));

  return (
    <>
      <tr className="align-top hover:bg-surface-1/40">
        <td className="px-3 py-2">
          <button type="button" onClick={onToggle} className="text-left min-w-0">
            <span className="text-t1 font-medium">{master.name}</span>
            <span className="block text-[11px] text-t4 truncate max-w-[280px]" title={master.master_key}>
              {master.category_label || '—'} · {master.rows.length} {master.rows.length === 1 ? 'γραμμή' : 'γραμμές'}
              {open ? ' ▾' : ' ▸'}
            </span>
          </button>
        </td>
        <td className="px-3 py-2 text-right text-t2 whitespace-nowrap">
          {master.stores > 0
            ? <span className="text-amber-700 dark:text-amber-300">{master.stores}</span>
            : <span className="text-t4">0</span>}
        </td>
        <td className="px-3 py-2 text-right text-t2 whitespace-nowrap tabular-nums">{platformText}</td>
        <td className="px-3 py-2 text-right text-t2 whitespace-nowrap tabular-nums">
          {catalogText}
          {master.reason && (
            <span className="block text-[10px] text-t4" title={PRICE_REASON_LABELS[master.reason]}>
              χωρίς τιμή καταλόγου
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
          <span className={master.delta === null
            ? 'text-t4'
            : (Math.abs(master.delta) >= 0.005 ? 'text-amber-700 dark:text-amber-300 font-semibold' : 'text-t4')}>
            {fmtDelta(master.delta)}
          </span>
          {master.wouldSend > 0 && (
            <span className="block text-[10px] text-t4" title="Πόσες γραμμές θα δημιουργήσει ο συγχρονισμός τιμών">
              θα σταλούν {master.wouldSend}
            </span>
          )}
        </td>
        <td className="px-3 py-2">
          <PriceDecision
            value={draft}
            dirty={dirty}
            disabled={!canDecide}
            catalogPrice={master.catalog_min}
            platformPrice={pr.min}
            sized={master.sized}
            onChange={onDraftChange}
          />
        </td>
      </tr>

      {open && (
        <tr className="bg-surface-1/30">
          <td colSpan={6} className="px-3 py-2">
            <div className="text-[11px] text-t4 mb-1 font-mono break-all">{master.master_key}</div>
            <table className="w-full text-xs">
              <tbody className="divide-y divide-line">
                {master.rows.map((r, i) => (
                  <tr key={`${r.store_id}-${r.item_code}-${r.option_code || ''}-${i}`}>
                    <td className="py-1 pr-2 text-t2 whitespace-nowrap">
                      {storeNames[String(r.store_id)] || `κατ. ${r.store_id}`}
                    </td>
                    <td className="py-1 pr-2 text-t3 truncate max-w-[260px]" title={r.item_code}>
                      {r.option_name
                        ? <span className="text-violet-700 dark:text-violet-300">μέγεθος: {r.option_name}</span>
                        : <span>{r.name}</span>}
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums text-t2">{fmtMoney(r.platform_price)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-t2">{fmtMoney(r.catalog_price)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">
                      <span className={r.delta !== null && Math.abs(Number(r.delta)) >= 0.005
                        ? 'text-amber-700 dark:text-amber-300' : 'text-t4'}>
                        {fmtDelta(r.delta === null || r.delta === undefined ? null : Number(r.delta))}
                      </span>
                    </td>
                    <td className="py-1 text-right whitespace-nowrap">
                      {r.would_send
                        ? <span className="px-1 rounded text-[10px] bg-amber-500/20 text-amber-800 dark:text-amber-200">θα σταλεί</span>
                        : <span className="px-1 rounded text-[10px] bg-surface-3 text-t3" title={
                          r.override_mode === 'keep_platform'
                            ? 'Απόφαση: κράτα την τιμή efood'
                            : (r.reason ? PRICE_REASON_LABELS[r.reason] : 'Καμία διαφορά')
                        }>
                          {r.override_mode === 'keep_platform' ? 'κρατιέται' : (r.reason ? 'εκτός σύγκρισης' : 'ίδια')}
                        </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
};

const PricesPanel: React.FC<Props> = ({
  stores, storeId, onStoreChange, onlyMismatch, onOnlyMismatchChange, q, onQChange,
  index, summary, total, loading, unavailable, requested = true, canDecide, canSync,
  draftFor, isDirty, onDraftChange, dirtyCount, saving, onSave, onDiscardDrafts, onReload, onOpenSync,
}) => {
  const [open, setOpen] = React.useState<Record<string, boolean>>({});

  const storeNames = useMemo(() => {
    const out: Record<string, string> = {};
    for (let i = 0; i < stores.length; i++) out[String(stores[i].store_id)] = stores[i].store_name;
    return out;
  }, [stores]);

  const masters = useMemo(() => {
    if (!index) return [] as PriceMasterInfo[];
    const keys = Object.keys(index.byMaster);
    const list: PriceMasterInfo[] = [];
    const needle = q.trim().toLowerCase();
    for (let i = 0; i < keys.length; i++) {
      const m = index.byMaster[keys[i]];
      if (needle) {
        const hay = `${m.name} ${m.category_label} ${m.master_key}`.toLowerCase();
        if (hay.indexOf(needle) === -1) continue;
      }
      list.push(m);
    }
    return sortMastersByDelta(list);
  }, [index, q]);

  const wouldSendTotal = useMemo(
    () => masters.reduce((sum, m) => sum + m.wouldSend, 0),
    [masters]
  );
  const keptTotal = useMemo(
    () => masters.filter(m => m.mode === 'keep_platform').length,
    [masters]
  );

  return (
    <div className="space-y-4">
      {/* Ο κανόνας, με τα ΜΕΤΡΗΜΕΝΑ νούμερα: χωρίς αυτά ο χειριστής δεν ξέρει αν
          μια διαφορά +0,10 είναι σφάλμα μας ή δική του παλιά απόφαση. */}
      <div className="p-3 rounded-xl border border-line bg-surface-1/50 text-xs text-t2 space-y-1">
        <div className="flex items-center gap-2 text-sm font-medium text-t1">
          <Coins className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          Τιμή καταλόγου = η τιμή του καναλιού <strong>delivery</strong>, αυτούσια
        </div>
        <p>
          <strong>Ποτέ</strong> δεν προστίθεται τέλος πλαστικού: η τιμή delivery το περιέχει ήδη.
          Στα είδη με μεγέθη η τιμή της επιλογής είναι <strong>απόλυτη</strong> και η τιμή του είδους είναι
          το φθηνότερο μέγεθος. Τα extra/σιρόπια κρατούν την τιμή της πλατφόρμας — ο κατάλογός μας δεν έχει
          αντίστοιχη τιμή και δεν την εφευρίσκουμε.
        </p>
        <p className="text-t3">
          Μέτρηση 17/9 στα 20 καταστήματα: 220 απλά είδη → 77 ταιριάζουν ακριβώς · 580 επιλογές μεγέθους →
          381 ταιριάζουν · κατανομή διαφοράς <strong>+0,10 ×140</strong> και <strong>−0,20 ×3</strong> ·
          «ταιριάζουν με delivery + τέλος: 0». Οι αποκλίσεις είναι <strong>συστηματικές σε ~17 προϊόντα</strong>
          {' '}(3 Milko + 4 Life εμφιαλωμένα, ~10 sandwich με τυρί, 3 cocktails 2ου τιμοκαταλόγου σε ένα
          κατάστημα) και είναι <strong>ίδιες και στα 20 καταστήματα</strong> — δηλαδή εμπορική απόφαση.
          Γι' αυτό η διόρθωση δεν γίνεται ποτέ μόνη της: αποφασίζεις εδώ, ανά είδος, μία φορά.
        </p>
      </div>

      {unavailable && (
        <div className="p-3 rounded-xl text-sm flex items-start gap-2 border bg-amber-50 dark:bg-amber-900/25 border-amber-300 dark:border-amber-700/50 text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="min-w-0">{unavailable}</span>
        </div>
      )}

      {/* Χειριστήρια */}
      <div className="flex flex-wrap items-end gap-3 bg-surface-1/40 border border-line rounded-xl p-3">
        <div className="min-w-[200px]">
          <label className="block text-xs text-t3 mb-1">Κατάστημα</label>
          <select
            value={storeId === null ? '' : String(storeId)}
            onChange={e => onStoreChange(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full bg-surface-0 border border-line-strong rounded-lg px-3 py-2 text-sm text-t1"
          >
            <option value="">Όλα τα καταστήματα</option>
            {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
          </select>
        </div>

        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs text-t3 mb-1">Αναζήτηση είδους</label>
          <div className="relative">
            <Search className="w-4 h-4 text-t4 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={q}
              onChange={e => onQChange(e.target.value)}
              placeholder="π.χ. milko"
              className="w-full bg-surface-0 border border-line-strong rounded-lg pl-9 pr-3 py-2 text-sm text-t1"
            />
          </div>
        </div>

        <label
          className="flex items-center gap-2 text-sm text-t2 h-[38px]"
          title="Ξετσέκαρέ το για να φορτωθεί ΟΛΗ η σύγκριση (και όσα ταιριάζουν) — πιο αργό, αλλά τότε το πλέγμα ξέρει και ποια συμφωνούν"
        >
          <input
            type="checkbox"
            checked={onlyMismatch}
            onChange={e => onOnlyMismatchChange(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          Μόνο όσα διαφέρουν
        </label>

        <button
          type="button"
          onClick={onReload}
          disabled={loading}
          className="px-3 py-2 text-sm bg-surface-2 hover:bg-surface-3 disabled:opacity-50 text-t1 rounded-lg flex items-center gap-2 h-[38px]"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Ανανέωση
        </button>

        <button
          type="button"
          onClick={onOpenSync}
          disabled={!canSync || loading || !index}
          title={canSync
            ? 'Δείχνει τι θα δημιουργηθεί ως πρόχειρη αλλαγή τιμής — καμία εξερχόμενη κλήση'
            : 'Χρειάζεται κατάσταση «επεξεργασία» και δικαίωμα σύνταξης'}
          className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center gap-2 h-[38px]"
        >
          <Coins className="w-4 h-4" /> Συγχρονισμός τιμών…
        </button>
      </div>

      {/* Σύνοψη του backend + οι μετρήσεις της σελίδας */}
      <div className="flex flex-wrap gap-4 text-xs text-t3">
        <span>Γραμμές σύγκρισης: <strong className="text-t2">{total || (index ? index.rows : 0)}</strong></span>
        {index && (
          index.complete
            ? (
              <span className="text-emerald-700 dark:text-emerald-300" title="Ό,τι δεν εμφανίζεται εδώ έχει συγκριθεί και συμφωνεί — το πλέγμα επιτρέπεται να γράψει «ίδια».">
                Πλήρης σύγκριση
              </span>
            ) : (
              <span
                className="text-t3"
                title={storeId !== null
                  ? 'Η σύγκριση αφορά ΜΟΝΟ το επιλεγμένο κατάστημα. Το πλέγμα δείχνει ΟΛΑ τα '
                    + 'καταστήματα, άρα γράφει «—», ΠΟΤΕ «ίδια»: για τα υπόλοιπα καταστήματα δεν ρωτήθηκε τίποτα.'
                  : 'Ήρθαν μόνο μέρος των γραμμών (φίλτρο «μόνο όσα διαφέρουν» ή κομμένη λίστα). '
                    + 'Το πλέγμα γράφει «—», ΠΟΤΕ «ίδια», για ό,τι δεν ήρθε.'}
              >
                Μερική σύγκριση
              </span>
            )
        )}
        <span>Είδη: <strong className="text-t2">{masters.length}</strong></span>
        {num(summary, 'match') !== null && <span>Ταιριάζουν: <strong className="text-t2">{num(summary, 'match')}</strong></span>}
        {num(summary, 'mismatch') !== null && (
          <span className="text-amber-700 dark:text-amber-300">Διαφέρουν: <strong>{num(summary, 'mismatch')}</strong></span>
        )}
        {num(summary, 'from_platform') !== null && (
          <span title="Γραμμές χωρίς τιμή καταλόγου: μένουν ως έχουν">
            Χωρίς σύγκριση: <strong className="text-t2">{num(summary, 'from_platform')}</strong>
          </span>
        )}
        <span title="Πόσες γραμμές θα φτιάξει ο συγχρονισμός τιμών">
          Θα σταλούν: <strong className="text-t2">{num(summary, 'would_send') !== null ? num(summary, 'would_send') : wouldSendTotal}</strong>
        </span>
        {keptTotal > 0 && (
          <span className="text-sky-700 dark:text-sky-300" title="Είδη με απόφαση «κράτα την τιμή efood»">
            Κρατιούνται: <strong>{keptTotal}</strong>
          </span>
        )}
      </div>

      {/* Μπάρα αποθήκευσης αποφάσεων */}
      {dirtyCount > 0 && (
        <div className="p-3 rounded-xl border border-blue-300 dark:border-blue-700/50 bg-blue-50 dark:bg-blue-900/20 flex flex-wrap items-center gap-3">
          <span className="text-sm text-blue-900 dark:text-blue-200">
            <strong>{dirtyCount}</strong> {dirtyCount === 1 ? 'απόφαση' : 'αποφάσεις'} χωρίς αποθήκευση.
            Η απόφαση «Ακολουθεί τον κατάλογο» <strong>σβήνει</strong> τη γραμμή (επιστροφή στην προεπιλογή).
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onDiscardDrafts}
              disabled={saving}
              className="px-3 py-2 bg-surface-2 hover:bg-surface-3 disabled:opacity-50 text-t1 border border-line-strong rounded-xl text-sm flex items-center gap-2"
            >
              <Undo2 className="w-4 h-4" /> Αναίρεση
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || !canDecide}
              title={canDecide ? 'Αποθήκευση των αποφάσεων' : 'Μόνο ο διαχειριστής του οργανισμού αποθηκεύει αποφάσεις τιμής'}
              className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Αποθήκευση αποφάσεων
            </button>
          </div>
        </div>
      )}

      {/* Ο πίνακας */}
      {loading && !index ? (
        <div className="text-center py-12 text-t3"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : masters.length === 0 ? (
        <div className="text-center py-12 text-t4 bg-surface-1/40 rounded-2xl border border-line px-4">
          {!requested ? (
            <div className="space-y-3">
              <p className="max-w-xl mx-auto">
                Η σύγκριση με τον κατάλογο δεν έχει φορτωθεί: διαβάζει όλα τα καταστήματα και δεν τρέχει
                από μόνη της. Ο πίνακας είναι άδειος <strong>επειδή δεν ρωτήθηκε κανείς</strong> — όχι
                επειδή συμφωνούν οι τιμές.
              </p>
              <button
                type="button"
                onClick={onReload}
                className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium inline-flex items-center gap-2"
              >
                <Coins className="w-4 h-4" /> Φόρτωση σύγκρισης
              </button>
            </div>
          ) : unavailable ? (
            'Η σύγκριση τιμών δεν είναι διαθέσιμη από αυτόν τον server.'
          ) : onlyMismatch ? (
            'Καμία διαφορά: οι τιμές του efood συμφωνούν με τον κατάλογο (delivery) σε ό,τι συγκρίθηκε.'
          ) : (
            'Καμία γραμμή σύγκρισης. Κάνε πρώτα εισαγωγή από το efood.'
          )}
        </div>
      ) : (
        <div className="overflow-x-auto border border-line rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-surface-1/60 text-t3 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2 min-w-[240px]">Είδος</th>
                <th className="text-right px-3 py-2">Καταστ. ≠</th>
                <th className="text-right px-3 py-2">Τιμή efood</th>
                <th className="text-right px-3 py-2">Τιμή καταλόγου</th>
                <th className="text-right px-3 py-2">Διαφορά</th>
                <th className="text-left px-3 py-2 min-w-[210px]">Απόφαση</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {masters.map(m => (
                <MasterRow
                  key={m.master_key}
                  master={m}
                  open={!!open[m.master_key]}
                  onToggle={() => setOpen(prev => {
                    const next = { ...prev };
                    if (next[m.master_key]) delete next[m.master_key];
                    else next[m.master_key] = true;
                    return next;
                  })}
                  storeNames={storeNames}
                  draft={draftFor(m.master_key)}
                  dirty={isDirty(m.master_key)}
                  canDecide={canDecide}
                  onDraftChange={onDraftChange}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-t4">
        Υπόμνημα αποφάσεων:{' '}
        <span className={`px-1 rounded ${OVERRIDE_MODE_CLASSES.catalog}`}>{OVERRIDE_MODE_LABELS.catalog}</span>{' '}
        = προεπιλογή, δεν αποθηκεύεται γραμμή ·{' '}
        <span className={`px-1 rounded ${OVERRIDE_MODE_CLASSES.keep_platform}`}>{OVERRIDE_MODE_LABELS.keep_platform}</span>{' '}
        = καμία αποστολή δεν την αλλάζει ποτέ ·{' '}
        <span className={`px-1 rounded ${OVERRIDE_MODE_CLASSES.fixed}`}>{OVERRIDE_MODE_LABELS.fixed}</span>{' '}
        = στέλνεται το ποσό που γράφεις. Το κλειδί είναι το κεντρικό είδος, όχι ο κωδικός efood: οι κωδικοί
        αλλάζουν όλοι στη μετάπτωση, η απόφαση επιβιώνει. Τα ονόματα καταστημάτων συντομεύονται ως{' '}
        {stores.length > 0 ? `«${storeShort(stores[0].store_name)}»` : '«…»'} στο πλέγμα.
      </p>
    </div>
  );
};

export default PricesPanel;
